// Shared client/server protocol. Imported by both the Worker and web/.

import type { Lang } from "./languages";

/**
 * Path the app is mounted on: it is served as part of the WorkBrawl hub at
 * https://workbrawl.com/guessbrawl/. Static files live in public/guessbrawl/ to match.
 */
export const BASE_PATH = "/guessbrawl";

export const WORD_LENGTH = 5;
export const MAX_ATTEMPTS = 6;

/**
 * The word of the day changes at midnight in this time zone, the same moment for everyone,
 * so a whole team always has the same word.
 */
export const DAILY_TIME_ZONE = "America/Guayaquil";
/** Day 1 of the word of the day: its number in the shared result counts from here. */
export const DAILY_EPOCH = "2026-10-14";

/** People per room. PLAYER_COLORS must have at least this many colors. */
export const MAX_PLAYERS = 12;

/** Round lengths the room creator can pick, in seconds. */
export const DURATIONS = [120, 180, 300] as const;
export type Duration = (typeof DURATIONS)[number];
export const DEFAULT_DURATION: Duration = 180;

export const COUNTDOWN_MS = 3000;
/** Longest a word stays open in a room; it also ends as soon as everyone is done. */
export const WORD_MS = 90_000;
/** How long the answer stays on screen before the next word. */
export const REVEAL_MS = 3000;
/** Points for solving a word: 6 at the first attempt, 1 at the sixth. */
export const solvePoints = (attempt: number): number => MAX_ATTEMPTS + 1 - attempt;
/** Extra for whoever solves a word first. */
export const FIRST_BONUS = 2;

/** 0: not in the word. 1: in the word, elsewhere. 2: right place. */
export type Mark = 0 | 1 | 2;

/**
 * lobby: waiting for someone to start. countdown: 3-2-1 before a round.
 * running: everyone races to find the same words while the clock counts down.
 * finished: the ranking is on screen.
 */
export type RoomStatus = "lobby" | "countdown" | "running" | "finished";

// Mid-tone colors that keep contrast on both light and dark backgrounds. Indexed by slot.
export const PLAYER_COLORS = [
  "#e5484d", // red
  "#0b84e5", // blue
  "#2f9e44", // green
  "#f08c00", // orange
  "#8e4ec6", // purple
  "#0f9d9a", // teal
  "#d6409f", // pink
  "#a0703c", // brown
  "#c79a00", // gold
  "#5b5bd6", // indigo
  "#7aa116", // lime
  "#6b7785", // slate
];

export interface PlayerInfo {
  id: string;
  name: string;
  slot: number;
  score: number;
  online: boolean;
}

export interface RankingEntry {
  id: string;
  name: string;
  slot: number;
  score: number;
}

/** Why a guess was not taken. */
export type GuessError = "LENGTH" | "NOT_A_WORD";

// ---------- Word of the day (HTTP, no room) ----------

/** POST /api/daily/guess */
export interface DailyGuessRequest {
  lang: Lang;
  guess: string;
}
export type DailyGuessResponse = { day: string; number: number; marks: Mark[] } | { error: GuessError };

/** POST /api/daily/answer: only answers once six real guesses have been used up. */
export interface DailyAnswerRequest {
  lang: Lang;
  guesses: string[];
}
export interface DailyAnswerResponse {
  day: string;
  answer: string;
}

/** GET /api/daily/today */
export interface DailyToday {
  day: string;
  number: number;
}

// ---------- Rooms ----------

export type ClientMsg = { type: "guess"; word: string } | { type: "start" } | { type: "ping" };

/** JSON body of POST /api/rooms. */
export interface CreateRoomRequest {
  duration?: Duration; // omitted = DEFAULT_DURATION
  lang?: Lang; // language of the words; omitted = English
  turnstileToken: string;
}

/** JSON body of POST /api/rooms/:code/verify (join by code from the home screen). */
export interface VerifyRoomRequest {
  turnstileToken: string;
}

/** Response of GET /api/config. */
export interface ClientConfig {
  turnstileSiteKey: string;
}

/** Error codes returned as `{ error }` by the HTTP API. */
export type ApiErrorCode = "INVALID_REQUEST" | "TURNSTILE_FAILED" | "ROOM_NOT_FOUND";

/** The current word, as one person sees it: their own guesses in full, the others' only as colors. */
export interface WordView {
  /** 1 for the first word of the round, 2 for the second… */
  n: number;
  /** open: guessing. reveal: the answer is on screen before the next word. */
  phase: "open" | "reveal";
  /** Milliseconds left for this word (open) or until the next one (reveal). */
  wordMs: number;
  mine: { guesses: string[]; marks: Mark[][]; done: boolean; solved: boolean };
  /** Everyone else: [slot, marks of each guess, solved]. */
  others: [number, Mark[][], boolean][];
  /** Shown only in the reveal. */
  answer: string | null;
  /** Slot of whoever solved this word first, or -1. */
  first: number;
}

export type ServerMsg =
  | {
      type: "state";
      you: string;
      status: RoomStatus;
      duration: Duration;
      lang: Lang;
      /** Milliseconds left in the countdown (status "countdown") or the round ("running"). */
      remainingMs: number;
      players: PlayerInfo[];
      /** Last finished round, best first. Empty before the first one ends. */
      ranking: RankingEntry[];
    }
  | ({
      type: "word";
      /** Milliseconds left in the round. */
      t: number;
      /** [slot, points] for every player. */
      p: [number, number][];
    } & WordView)
  | { type: "invalid"; error: GuessError }
  | { type: "player_joined"; player: PlayerInfo }
  | { type: "player_updated"; player: PlayerInfo }
  | { type: "player_left"; id: string }
  | { type: "error"; code: ErrorCode; message: string }
  | { type: "pong" };

/**
 * Exact keep-alive frames. The room registers them as a WebSocket auto-response, so
 * Cloudflare answers pings at the edge WITHOUT waking the Durable Object: an idle room
 * with open tabs stays hibernated (no billable duration). The client must send PING_FRAME
 * byte for byte, or the ping falls through to the room and wakes it.
 */
export const PING_FRAME = '{"type":"ping"}';
export const PONG_FRAME = '{"type":"pong"}';

export type ErrorCode = "ROOM_FULL" | "ROOM_NOT_FOUND" | "BAD_MESSAGE";

export const ROOM_CODE_ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
export const ROOM_CODE_LENGTH = 5;
export const NAME_MAX_LENGTH = 20;

const ROOM_CODE_RE = new RegExp(`^[${ROOM_CODE_ALPHABET}]{${ROOM_CODE_LENGTH}}$`);
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isValidRoomCode(code: string): boolean {
  return ROOM_CODE_RE.test(code);
}

export function isValidPlayerId(id: string): boolean {
  return UUID_RE.test(id);
}

export function isDuration(value: unknown): value is Duration {
  return (DURATIONS as readonly unknown[]).includes(value);
}

/**
 * The form a word is compared in: lowercase, accents and diaereses removed (á → a, ü → u),
 * ñ kept as its own letter.
 */
export function normalizeWord(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/ñ/g, "\u0000")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/\u0000/g, "ñ");
}

/** The letters of a language's keyboard, in QWERTY rows. Spanish adds Ñ after L. */
export function keyboardRows(lang: Lang): string[][] {
  return [
    "qwertyuiop".split(""),
    (lang === "es" ? "asdfghjklñ" : "asdfghjkl").split(""),
    "zxcvbnm".split(""),
  ];
}

/**
 * Colors for a guess against the answer (both normalized), with the usual rule for repeated
 * letters: right places first, then each remaining letter of the answer can only light up
 * one "elsewhere" mark.
 */
export function scoreGuess(guess: string, answer: string): Mark[] {
  const g = [...guess];
  const a = [...answer];
  const marks: Mark[] = g.map(() => 0);
  const left = new Map<string, number>();
  g.forEach((letter, i) => {
    if (letter === a[i]) marks[i] = 2;
    else left.set(a[i]!, (left.get(a[i]!) ?? 0) + 1);
  });
  g.forEach((letter, i) => {
    if (marks[i] === 2) return;
    const count = left.get(letter) ?? 0;
    if (count > 0) {
      marks[i] = 1;
      left.set(letter, count - 1);
    }
  });
  return marks;
}

/**
 * Trims and collapses whitespace. Returns null when the name is empty, longer than
 * NAME_MAX_LENGTH characters, or contains HTML-ish or control characters.
 */
export function sanitizeName(raw: string): string | null {
  const name = raw.trim().replace(/\s+/g, " ");
  const length = [...name].length;
  if (length < 1 || length > NAME_MAX_LENGTH) return null;
  if (/[<>&"'`\p{Cc}]/u.test(name)) return null;
  return name;
}

/** Best score first; ties keep the lower slot (who joined earlier) ahead. */
export function rank<T extends { score: number; slot: number }>(players: readonly T[]): T[] {
  return [...players].sort((a, b) => b.score - a.score || a.slot - b.slot);
}
